import {
  useAmendReceiptRoomBookingItemsMutation,
  useReceiptRoomBookingAmendContextQuery,
} from "@/modules/billing/presentation/api/queries";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { Button, Checkbox, Input, Modal, toast } from "@devhop/ui";
import { Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReceiptRoomBookingAmendContextDTO } from "../api/client";

type EditReceiptAssistItemsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiptId: string;
  onSuccess?: () => void;
};

type PriceRow = {
  instructorId: string;
  instructorName: string;
  sessionCount: number;
  pricePerSession: number;
};

function buildAssistRows(context: ReceiptRoomBookingAmendContextDTO): PriceRow[] {
  return context.assistRows.map((row) => ({
    instructorId: row.instructorId,
    instructorName: row.instructorName,
    sessionCount: row.sessionCount,
    pricePerSession: row.pricePerSession,
  }));
}

function activeSessionIds(context: ReceiptRoomBookingAmendContextDTO): string[] {
  return context.sessionRows
    .filter((s) => s.status !== "cancelled")
    .map((s) => s.bookingId);
}

export function EditReceiptAssistItemsDialog({
  open,
  onOpenChange,
  receiptId,
  onSuccess,
}: EditReceiptAssistItemsDialogProps) {
  const { data: context, isLoading } = useReceiptRoomBookingAmendContextQuery(
    receiptId,
    open,
  );
  const amendMutation = useAmendReceiptRoomBookingItemsMutation(receiptId);
  const [assistRows, setAssistRows] = useState<PriceRow[]>([]);
  const [includedBookingIds, setIncludedBookingIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (open && context) {
      setAssistRows(buildAssistRows(context));
      setIncludedBookingIds(new Set(activeSessionIds(context)));
    }
  }, [open, context]);

  const cancelBookingIds = useMemo(() => {
    if (!context) return [];
    return activeSessionIds(context).filter((id) => !includedBookingIds.has(id));
  }, [context, includedBookingIds]);

  const keptSessionCount = useMemo(() => {
    if (!context) return 0;
    return activeSessionIds(context).length - cancelBookingIds.length;
  }, [context, cancelBookingIds.length]);

  const previewRoomTotal = useMemo(() => {
    if (!context) return 0;
    return keptSessionCount * context.roomUnitPrice;
  }, [context, keptSessionCount]);

  const previewAssistTotal = useMemo(() => {
    if (!context?.canAmendAssistItems) return context?.assistItemsTotal ?? 0;
    return assistRows.reduce(
      (sum, row) => sum + row.pricePerSession * keptSessionCount,
      0,
    );
  }, [assistRows, context, keptSessionCount]);

  const previewTotal = previewRoomTotal + previewAssistTotal;

  const canOpenEditor =
    context != null &&
    context.baseBlockReason == null &&
    (context.canAmendSessions || context.canAmendAssistItems);

  const hasChanges = useMemo(() => {
    if (!context) return false;
    if (context.needsReceiptCalendarSync) return true;
    const sessionChanged = cancelBookingIds.length > 0;
    if (sessionChanged) return true;
    if (previewTotal !== context.currentPaymentAmount) return true;
    if (!context.canAmendAssistItems) return false;
    return assistRows.some((row) => {
      const original = context.assistRows.find(
        (r) => r.instructorId === row.instructorId,
      );
      return original?.pricePerSession !== row.pricePerSession;
    });
  }, [
    assistRows,
    cancelBookingIds.length,
    context,
    previewTotal,
  ]);

  const toggleSession = (bookingId: string, checked: boolean) => {
    setIncludedBookingIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(bookingId);
      else next.delete(bookingId);
      return next;
    });
  };

  const handlePriceChange = (instructorId: string, raw: string) => {
    const pricePerSession = Math.max(
      0,
      Number.parseInt(raw.replace(/\D/g, ""), 10) || 0,
    );
    setAssistRows((prev) =>
      prev.map((row) =>
        row.instructorId === instructorId
          ? { ...row, pricePerSession }
          : row,
      ),
    );
  };

  const handleSubmit = async () => {
    if (!context || !hasChanges) return;
    if (keptSessionCount < 1) {
      toast.error("ຕ້ອງເຫຼືອຢ່າງໜ້ອຍ 1 ຄັ້ງໃນໃບເກັບເງິນ");
      return;
    }

    try {
      const body: {
        cancelBookingIds?: string[];
        assistItems?: Array<{ instructorId: string; pricePerSession: number }>;
        syncFromCalendar?: boolean;
      } = {};

      if (
        context.needsReceiptCalendarSync &&
        cancelBookingIds.length === 0
      ) {
        body.syncFromCalendar = true;
      }

      if (cancelBookingIds.length > 0) {
        body.cancelBookingIds = cancelBookingIds;
      }

      if (context.canAmendAssistItems && assistRows.length > 0) {
        const assistChanged = assistRows.some((row) => {
          const original = context.assistRows.find(
            (r) => r.instructorId === row.instructorId,
          );
          return original?.pricePerSession !== row.pricePerSession;
        });
        if (assistChanged || cancelBookingIds.length > 0) {
          body.assistItems = assistRows.map((row) => ({
            instructorId: row.instructorId,
            pricePerSession: row.pricePerSession,
          }));
        }
      }

      const result = await amendMutation.mutateAsync(body);
      toast.success(
        `ອັບເດດລາຍການແລ້ວ · ຍອດໃໝ່ ${result.newPaymentAmount.toLocaleString("lo-LA")} ກີບ`,
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ອັບເດດລາຍການບໍ່ສຳເລັດ",
      );
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ແກ້ໄຂລາຍການຈອງຫ້ອງ"
      size="md"
    >
      {isLoading || !context ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
          <Loader2Icon className="mr-2 size-4 animate-spin" />
          ກຳລັງໂຫຼດ...
        </div>
      ) : !canOpenEditor ? (
        <p className="py-4 text-muted-foreground text-sm">
          {context.baseBlockReason ?? "ບໍ່ສາມາດແກ້ໄຂໃບເກັບເງິນນີ້"}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{context.receiptNo}</p>
            <p className="text-muted-foreground text-xs">
              ຄ່າຈອງ {context.roomUnitPrice.toLocaleString("lo-LA")} ກີບ/ຄັ້ງ
            </p>
          </div>

          {context.canAmendSessions ? (
            <div className="space-y-2">
              <p className="font-medium text-sm">
                ເລືອກວັນທີທີ່ຢູ່ໃນໃບເກັບເງິນ (ຖອດເຄື່ອງ = ຍົກເລີກຄັ້ງນັ້ນ)
              </p>
              <div className="max-h-56 space-y-2 overflow-y-auto rounded-md border p-2">
                {context.sessionRows
                  .filter((s) => s.status !== "cancelled")
                  .map((session) => {
                    const checked = includedBookingIds.has(session.bookingId);
                    const dayNum = Number(session.date.slice(8, 10));
                    return (
                      <label
                        key={session.bookingId}
                        className="flex cursor-pointer items-start gap-3 rounded-md border p-2 hover:bg-muted/40"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={amendMutation.isPending}
                          onCheckedChange={(v) =>
                            toggleSession(session.bookingId, v === true)
                          }
                        />
                        <div className="min-w-0 flex-1 text-sm">
                          <p className="font-medium">
                            {formatDate(session.date)}
                            {Number.isFinite(dayNum) ? ` (ວັນທີ ${dayNum})` : ""}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {session.roomName} ·{" "}
                            {formatTime(session.startTime)}–
                            {formatTime(session.endTime)}
                          </p>
                        </div>
                      </label>
                    );
                  })}
              </div>
              {cancelBookingIds.length > 0 && (
                <p className="text-amber-700 text-xs dark:text-amber-400">
                  ຈະຕັດ {cancelBookingIds.length} ຄັ້ງອອຈາກໃບເກັບເງິນ ແລະ
                  ຍົກເລີກການຈອງໃນປະຕິທິນ
                </p>
              )}
              {context.needsReceiptCalendarSync && (
                <p className="text-destructive text-xs">
                  {context.missingSessionDayNumbers.length > 0 ? (
                    <>
                      ວັນທີ {context.missingSessionDayNumbers.join(", ")}{" "}
                      ຍົກເລີກໃນປະຕິທິນແລ້ວ ແຕ່ຍັງຢູ່ໃນບິນ —{" "}
                    </>
                  ) : (
                    <>ບິນບໍ່ຕົງກັບຈຳນວນຄັ້ງໃນປະຕິທິນ — </>
                  )}
                  ກົດ「ບັນທຶກລາຍການ」ເພື່ອຕັດອອກ ແລະ ປັບຍອດເປັນ{" "}
                  {previewTotal.toLocaleString("lo-LA")} ກີບ
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              {context.sessionAmendBlockReason}
            </p>
          )}

          {context.canAmendAssistItems && assistRows.length > 0 ? (
            <div className="space-y-3 border-t pt-4">
              <p className="font-medium text-sm">ຄ່າຄູຊ່ວຍ (ລາຄາ/ຄັ້ງ)</p>
              {assistRows.map((row) => (
                <div
                  key={row.instructorId}
                  className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{row.instructorName}</p>
                    <p className="text-muted-foreground text-xs">
                      ຄິດຕາມ {keptSessionCount} ຄັ້ງທີ່ເຫຼືອ
                    </p>
                  </div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    className="w-full sm:w-40"
                    value={
                      row.pricePerSession > 0 ? String(row.pricePerSession) : ""
                    }
                    placeholder="ລາຄາ (ກີບ)"
                    disabled={amendMutation.isPending}
                    onChange={(e) =>
                      handlePriceChange(row.instructorId, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          ) : null}

          <div className="space-y-1 rounded-md border p-3 text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>ຄ່າຈອງ ({keptSessionCount} ຄັ້ງ)</span>
              <span>{previewRoomTotal.toLocaleString("lo-LA")} ກີບ</span>
            </div>
            {context.canAmendAssistItems && assistRows.length > 0 ? (
              <div className="flex justify-between text-muted-foreground">
                <span>ຄ່າຄູຊ່ວຍ</span>
                <span>{previewAssistTotal.toLocaleString("lo-LA")} ກີບ</span>
              </div>
            ) : null}
            <div className="flex justify-between border-t pt-2 font-semibold">
              <span>ຍອດລວມໃໝ່</span>
              <span>{previewTotal.toLocaleString("lo-LA")} ກີບ</span>
            </div>
            {context.currentPaymentAmount !== previewTotal && (
              <p className="text-muted-foreground text-xs">
                ຍອດເດີມ: {context.currentPaymentAmount.toLocaleString("lo-LA")}{" "}
                ກີບ
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={amendMutation.isPending}
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={amendMutation.isPending || !hasChanges}
              onClick={() => void handleSubmit()}
            >
              {amendMutation.isPending
                ? "ກຳລັງບັນທຶກ..."
                : context.needsReceiptCalendarSync &&
                    cancelBookingIds.length === 0
                  ? "ບັນທຶກ & ປັບບິນ"
                  : "ບັນທຶກລາຍການ"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
